import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";
import { Crown, X, Coins } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface PremiumFeatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  description?: string;
  icon?: ReactNode;
  user?: {
    email?: string;
    name?: string;
    isPremium?: boolean;
  } | null;
  onPromptLogin?: () => void;
  onUpgradeToPremium?: () => void;
}

export const PremiumFeatureModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  description,
  icon,
  user,
  onPromptLogin,
  onUpgradeToPremium,
}: PremiumFeatureModalProps) => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handlePurchaseSubscription = (_plan: 'monthly' | 'yearly', _paymentMethod: string) => {
    if (onUpgradeToPremium) {
      onUpgradeToPremium();
    }
  };

  const handleUpgradeClick = () => {
    if (!user) {
      if (onPromptLogin) onPromptLogin();
    } else {
      setShowPaymentModal(true);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="p-0 max-w-md bg-transparent border-none overflow-hidden h-screen top-0 translate-y-0"
        hideCloseButton
      >
        <div className="bg-gradient-dreamy min-h-full flex flex-col animate-fade-in">
          {/* Header: grows into whatever the plans and the button leave over,
              so the sheet has no dead space under the button. */}
          <div
            className="relative flex-1 flex flex-col justify-end px-6 pt-16 pb-10 overflow-hidden"
            style={{ paddingTop: "calc(4rem + var(--safe-area-inset-top, 0px))" }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-primary-glow/20 to-transparent" />
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white leading-tight">
                  {title}
                </h1>
                {subtitle && (
                  <h2 className="text-xl font-light text-primary-glow mt-1">
                    {subtitle}
                  </h2>
                )}
              </div>
              <div className="ml-4 shrink-0 text-amber-400">
                {icon ?? <Crown className="h-12 w-12" />}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 z-20 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors"
              style={{ top: "calc(1rem + var(--safe-area-inset-top, 0px))" }}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Main Content */}
          <div
            className="px-6 pb-6 overflow-y-auto relative shrink-0"
            style={{ paddingBottom: "calc(1.5rem + var(--safe-area-inset-bottom, 0px))" }}
          >
            <div className="max-w-2xl mx-auto">
              <div className="space-y-4">
                {description && (
                  <p className="text-sm text-white/90 text-center py-4">
                    {description}
                  </p>
                )}

                {/* Features Section */}
                <div className="grid grid-cols-2 gap-3 text-base">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                      <span className="text-lg">💬</span>
                      <span className="text-sm font-medium text-white">{t('premium_modal.unlimited_chats')}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                      <span className="text-lg">👑</span>
                      <span className="text-sm font-medium text-white">{t('premium_modal.premium_bfs_short')}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                      <span className="text-lg">🔥</span>
                      <span className="text-sm font-medium text-white">{t('premium_modal.private_photos')}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                      <Coins className="h-5 w-5 text-amber-400" />
                      <span className="text-sm font-medium text-white">{t('premium_modal.free_tokens_100')}</span>
                    </div>
                  </div>
                </div>

                {/* Pricing Plans */}
                <div className="grid grid-cols-2 gap-3">
                  <Card
                    className={`p-2 text-center border-2 cursor-pointer transition-all hover:scale-105 ${
                      selectedPlan === 'monthly'
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg'
                        : 'border-primary/20 hover:border-primary/40'
                    }`}
                    onClick={() => setSelectedPlan('monthly')}
                  >
                    <h3 className="text-lg font-bold mb-2">{t('premium_modal.monthly')}</h3>
                    <div className="mb-2">
                      <p className="text-xs text-muted-foreground line-through">$19.99</p>
                      <p className="text-2xl font-bold text-primary">$9.99</p>
                      <p className="text-xs text-muted-foreground">{t('premium_modal.per_month')}</p>
                    </div>
                  </Card>

                  <Card
                    className={`p-2 pt-4 text-center border-2 cursor-pointer transition-all hover:scale-105 relative ${
                      selectedPlan === 'yearly'
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg'
                        : 'border-primary/20 hover:border-primary/40'
                    }`}
                    onClick={() => setSelectedPlan('yearly')}
                  >
                    <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                      <span className="bg-white text-pink-500 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap">
                        {t('premium_modal.save_50')}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold mb-2">{t('premium_modal.yearly')}</h3>
                    <div className="mb-2">
                      <p className="text-xs text-muted-foreground line-through">$159.99</p>
                      <p className="text-2xl font-bold text-primary">$79.99</p>
                      <p className="text-xs text-muted-foreground">{t('premium_modal.per_year')}</p>
                    </div>
                  </Card>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="romantic"
                    size="lg"
                    className="w-full py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                    onClick={handleUpgradeClick}
                  >
                    <Crown className="h-4 w-4 mr-2" />
                    {t('premium_modal.upgrade_to_plan', {
                      plan: selectedPlan === 'monthly'
                        ? t('premium_modal.monthly_plan')
                        : t('premium_modal.yearly_plan'),
                    })}
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">
                    {t('premium_modal.secure_payment_cancel')}
                  </p>
                </div>
              </div>
            </div>

            <SubscriptionPaymentModal
              open={showPaymentModal}
              onOpenChange={setShowPaymentModal}
              selectedPlan={selectedPlan}
              onPurchaseSubscription={handlePurchaseSubscription}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
